<template>
  <div class="planCard" :class="{preview: isPreview, [name]: true}">
    <div class="planCardInner">
      <div class="d-flex align-center ga-2 mb-5">
        <p class="name">{{ name }}</p>
        <div v-if="popular" class="badge">Most popular</div>
      </div>

      <p v-if="!!monthlyPrice && !!yearlyPrice" class="price mb-1">$
        {{ pricePerMonth }}
        <span class="priceInfo"> / month / user</span>
      </p>
      <p v-else class="price mb-1">Custom pricing</p>
      <p class="billingNote mb-5" :class="{ hidden: !(monthlyPrice && yearlyPrice && type === 'yearly') }">
        ${{ yearlyPrice }} per user, billed annually
      </p>

      <p class="description">{{ description }}</p>

      <v-btn
        size="large"
        variant="flat"
        class="w-100 dark-button mb-3"
        color="var(--dark-5, #1C1F1D)"
        @click="$emit('onSelect')"
        :disabled="disabled"
      >
        <template v-if="!!monthlyPrice && !!yearlyPrice">
          <template v-if="currentPlanName">
            {{ changePlanLabel }}
          </template>
          <template v-else-if="canStartTrial">
            Start free trial
          </template>
          <template v-else>
            Choose {{ name }}
          </template>
        </template>
        <template v-else>
          Talk to sales
        </template>
      </v-btn>
      <p class="buttonNote mb-8">{{ buttonNote }}</p>

      <div class="divider"/>

      <p class="includes">
        <template v-if="!!monthlyPrice && !!yearlyPrice">
          <template v-if="name === 'Basic'">
            This includes:
          </template>
          <template v-else>
            Everything in Basic, plus:
          </template>
        </template>
        <template v-else>
          Everything in Pro, plus:
        </template>
      </p>

      <ul class="features">
        <li v-for="feature in features.slice(0, isPreview ? 3 : undefined)" :key="feature" class="featureItem">
          <v-icon size="large" :color="checkIconColor">mds:check</v-icon>
          <span class="featureItemLabel">{{ feature }}</span>
        </li>
        <li v-if="isPreview && brand.links.pricing" class="seeAllFeatures" @click="openPricingPage">
          See all features
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { PLANS, ENTERPRISE_PLAN, canStartCardlessTrial, getTrialEndDate } from '@/utils/payments'
import { isDarkTheme } from '@/composables/useAppTheme'
import brand from '@/composables/useBrand'

export default {
  name: 'PlanCard',
  props: {
    name: String,
    users: String,
    description: String,
    monthlyPrice: Number,
    yearlyPrice: Number,
    type: String,
    popular: Boolean,
    isPreview: Boolean,
    disabled: Boolean,
    currentPlanName: String,
    currentSubscriptionType: String
  },
  computed: {
    brand: () => brand,
    pricePerMonth: function () {
      return this.type === 'yearly' ? Math.round(this.yearlyPrice / 12) : this.monthlyPrice
    },
    ...mapState({
      activeSubscription: state => state.payments.activeSubscription,
      currentUser: state => state.user.user
    }),
    plan: function () {
      return PLANS.find(p => p.name === this.name)
    },
    canStartTrial: function () {
      return canStartCardlessTrial({
        plan: this.plan,
        activeSubscription: this.activeSubscription,
        currentUser: this.currentUser
      })
    },
    changePlanLabel: function () {
      if (this.currentPlanName !== this.name) {
        return `Change to ${this.name}`
      }

      if (this.currentSubscriptionType && this.currentSubscriptionType !== this.type) {
        return this.type === 'yearly' ? 'Switch to annual' : 'Switch to monthly'
      }

      return `Keep using ${this.name}`
    },
    buttonNote: function () {
      if (this.name === 'Enterprise') {
        return 'A 30 minute call'
      }

      if (this.canStartTrial) {
        return this.name === 'Basic' ? '14 free days of Pro first · no card · cancel any time' : '14 days free · no card · cancel any time'
      }

      const trialEndDate = getTrialEndDate(this.activeSubscription)

      return trialEndDate ? `No charge until ${trialEndDate} · cancel any time` : null
    },
    features: function () {
      if (this.name === 'Enterprise') {
        return ENTERPRISE_PLAN.features
      } else {
        if (this.plan) {
          return this.plan.features
        }
      }
      return []
    },
    checkIconColor () {
      return isDarkTheme() ? '#E8EAF0' : '#1C1F1D'
    }
  },
  methods: {
    openPricingPage () {
      window.open(brand.links.pricing)
    }
  }
}
</script>

<style scoped>
.planCard {
  width: 100%;
  flex-grow: 1;
  flex-shrink: 0;
  max-width: 443px;

  &.Basic {
    .planCardInner {
      border: 2px solid var(--plan-card-basic, #3BB0E6);
    }
  }

  &.Pro {
    .planCardInner {
      border: 2px solid var(--plan-card-pro, #A0F090);
    }
  }

  &.Enterprise {
    .planCardInner {
      border: 2px solid var(--plan-card-enterprise, #A091FF);
    }
  }

  &.preview .planCardInner {
    height: 100%;
    min-height: initial;
  }
}

.badge {
  padding: 4px 8px;
  font-size: 13px;
  font-weight: 400;
  margin-left: 8px;
  line-height: 18.2px;
  border-radius: 3px;
  display: inline-block;
  letter-spacing: -0.02em;
  color: #1C1F1D;
  background-color: var(--plan-card-pro, #A0F090);
}

.planCardInner {
  width: 100%;
  padding: 24px;
  min-height: 734px;
  border-radius: 20px;
  background-color: var(--white, #FFF);
}

.name {
  font-size: 32px;
  font-weight: 500;
  line-height: 38.4px;
  letter-spacing: -0.03em;
  font-family: Faktum, sans-serif;
}

.price {
  font-size: 40px;
  font-weight: 400;
  line-height: 48px;
  letter-spacing: -0.03em;
  color: var(--dark-5, #1C1F1D);
  font-family: Faktum, sans-serif;
}

.billingNote {
  font-size: 13px;
  line-height: 18px;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.billingNote.hidden {
  visibility: hidden;
}

.priceInfo {
  font-size: 12px;
  font-weight: 400;
  color: #1C1F1DCC;
  line-height: 15px;
  letter-spacing: -0.03em;
  font-family: Inter, sans-serif;
}

.buttonNote {
  min-height: 20px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.02em;
  color: var(--dark-3, #61676E);
}

.description {
  font-size: 16px;
  font-weight: 400;
  min-height: 52px;
  line-height: 25.6px;
  margin-bottom: 32px;
  letter-spacing: -0.02em;
  color: var(--dark-5, #1C1F1D);
}

:global(.v-btn.v-btn--size-large.v-btn--variant-flat) {
  &.dark-button {
    font-size: 16px;
    font-weight: 500;
    line-height: 19.2px;
    letter-spacing: -0.02em;
    font-family: Faktum, sans-serif;
    border-radius: 58px !important;
    background-color: var(--dark-5, #1C1F1D) !important;
  }

  /* The forced background above also paints the disabled state, so it has to be dimmed here. */
  &.dark-button.v-btn--disabled {
    opacity: 0.38;
  }
}

.divider {
  width: 100%;
  height: 1px;
  margin-bottom: 24px;
  background-color: var(--light-5, #C8CCCA)
}

.includes {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 24px;
  line-height: 25.6px;
  letter-spacing: -0.02em;
  color: var(--dark-5, #1C1F1D);
}

.features {
  gap: 16px;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  list-style-type: none;
}

.featureItem {
  gap: 12px;
  display: flex;
  align-items: center;
}

.featureItemLabel {
  font-size: 16px;
  font-weight: 400;
  font-family: Inter;
  line-height: 25.6px;
  letter-spacing: -0.02em;
  color: var(--dark-5, #1C1F1D);
}

.seeAllFeatures {
  font-size: 12px;
  cursor: pointer;
  line-height: 15px;
  font-weight: 400;
  padding-left: 32.5px;
  color: var(--blue-3, #4285F4);
}
</style>
